import { isDesignSystemProject } from "../components/design-system-project";
import type { Project, ProjectFile } from "../types";
import {
  isCanonicalDeckProjectPath,
  isEmbedReferenceSourceFile,
  isRootHtmlMatchingReferenceSource,
  isRootNonDeckHtmlWhenRefsPresent,
} from "./branding/embedDeliverableFilePolicy";

export type ProjectCoverFile = {
  kind: "html" | "image" | "video" | "logo";
  name: string;
  /** File mtime ms from cover-hints — appended as `?v=` on card media URLs. */
  version?: number;
};

export function findDesignSystemLogoFile(files: ProjectFile[]): ProjectFile | null {
  const logoCandidates = files
    .filter((file) => file.type !== "dir")
    .filter((file) => {
      const name = file.path ?? file.name;
      return file.kind === "image" || /\.(svg|png|jpe?g|webp|gif)$/iu.test(name);
    });
  return (
    logoCandidates.find((file) => (file.path ?? file.name).toLowerCase() === "assets/logo.svg") ??
    logoCandidates.find((file) =>
      /(^|\/)(logo|wordmark|brand-mark|brandmark|mark|icon|favicon)[^/]*\.(svg|png|jpe?g|webp|gif)$/iu.test(
        file.path ?? file.name,
      ),
    ) ??
    null
  );
}

function coverHtmlPath(file: ProjectFile): string {
  return (file.path ?? file.name).replace(/\\/g, "/").replace(/^\.\/+/, "");
}

function isExcludedProjectCoverHtml(
  file: ProjectFile,
  files: readonly ProjectFile[],
): boolean {
  if (isEmbedReferenceSourceFile(file)) return true;
  if (isRootHtmlMatchingReferenceSource(file, files)) return true;
  if (isRootNonDeckHtmlWhenRefsPresent(file, files)) return true;
  return false;
}

function isDeckCoverHtml(file: ProjectFile): boolean {
  return isCanonicalDeckProjectPath(coverHtmlPath(file));
}

/** Latest cover candidate for project cards (HTML/image/video/logo). */
export function pickProjectCoverFile(
  project: Project,
  files: ProjectFile[],
): ProjectCoverFile | null {
  const designSystemProject = isDesignSystemProject(project);
  if (designSystemProject) {
    const logo = findDesignSystemLogoFile(files);
    if (logo) {
      return { kind: "logo", name: logo.path ?? logo.name };
    }
    return null;
  }

  // Non-deck projects with a pinned entryFile resolve the cover from metadata.
  // Deck projects always pick from files (prefer deck*.html, exclude Canvas
  // leaks) so cover-hints /files can supply coverVersion + correct path.
  const isDeckProject =
    project.metadata?.kind === "deck" || project.metadata?.skipDiscoveryBrief === true;
  if (!isDeckProject && project.metadata?.entryFile?.trim()) {
    return null;
  }

  const htmlCandidates = files.filter(
    (file) => file.kind === "html" && !isExcludedProjectCoverHtml(file, files),
  );
  const deckHtml = htmlCandidates
    .filter(isDeckCoverHtml)
    .sort((a, b) => b.mtime - a.mtime)[0];
  const html =
    deckHtml
    ?? htmlCandidates.find((file) => coverHtmlPath(file) === "index.html")
    ?? htmlCandidates.sort((a, b) => b.mtime - a.mtime)[0];
  if (html) {
    return { kind: "html", name: coverHtmlPath(html) };
  }

  const image = files
    .filter((file) => file.kind === "image")
    .sort((a, b) => b.mtime - a.mtime)[0];
  if (image) {
    return { kind: "image", name: image.path ?? image.name };
  }

  const video = files
    .filter((file) => file.kind === "video")
    .sort((a, b) => b.mtime - a.mtime)[0];
  if (video) {
    return { kind: "video", name: video.path ?? video.name };
  }

  return null;
}

/** Base file name for in-app HTML/deck preview deep-link from project cards. */
export function projectPreviewDeepLinkFileName(
  project: Project,
  cover: ProjectCoverFile | null | undefined,
): string | null {
  if (cover?.kind === "html") {
    return basename(cover.name);
  }
  const entry = project.metadata?.entryFile;
  if (entry && /\.html?$/i.test(entry)) {
    return basename(entry);
  }
  return null;
}

/** `onOpen` options when a project card should land on the HTML preview tab. */
export function projectOpenOptionsFromPreviewCover(
  project: Project,
  cover: ProjectCoverFile | null | undefined,
): { fileName: string } | undefined {
  const fileName = projectPreviewDeepLinkFileName(project, cover);
  return fileName ? { fileName } : undefined;
}

export function projectCoverFilesEqual(
  left: ProjectCoverFile | null | undefined,
  right: ProjectCoverFile | null | undefined,
): boolean {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return left.kind === right.kind && left.name === right.name && left.version === right.version;
}

function basename(path: string): string {
  return path.split("/").pop() || path;
}
